import React, { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { NavLink, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { apiRequest, hasApiScope } from "../authConfig";
import { fetchClienti, fetchRapportini } from "../api";
import Header from "./Header";
import RapportiniTable from "./RapportiniTable";
import CommessePage from "./CommessePage";
import "./Dashboard.css";

function Dashboard() {
  const { instance, accounts } = useMsal();
  const [clienti, setClienti] = useState([]);
  const [selectedClienteId, setSelectedClienteId] = useState(null);
  const [rapportini, setRapportini] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientError, setClientError] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const loadClienti = async () => {
      try {
        setClientError(null);
        if (!hasApiScope) {
          throw new Error("API scope non configurato. Imposta REACT_APP_API_SCOPE con lo scope dell'App Registration del backend.");
        }
        let tokenResponse;
        try {
          tokenResponse = await instance.acquireTokenSilent({
            ...apiRequest,
            account: accounts[0],
          });
        } catch (silentError) {
          tokenResponse = await instance.acquireTokenPopup(apiRequest);
        }

        const clientiData = await fetchClienti(tokenResponse.accessToken);
        setClienti(clientiData);
        if (clientiData.length > 0) {
          setSelectedClienteId((current) => current ?? clientiData[0].id);
        } else {
          setError("Nessun cliente assegnato all'utente.");
        }
      } catch (err) {
        console.error("Errore nel caricamento dei clienti:", err);
        setClientError("Impossibile caricare i clienti. Verifica la connessione.");
      }
    };

    if (accounts.length > 0) {
      loadClienti();
    }
  }, [instance, accounts]);

  useEffect(() => {
    const loadRapportini = async () => {
      if (!selectedClienteId) {
        return;
      }

      try {
        setLoading(true);
        setError(null);
        if (!hasApiScope) {
          throw new Error("API scope non configurato. Imposta REACT_APP_API_SCOPE con lo scope dell'App Registration del backend.");
        }

        let tokenResponse;
        try {
          tokenResponse = await instance.acquireTokenSilent({
            ...apiRequest,
            account: accounts[0],
          });
        } catch (silentError) {
          tokenResponse = await instance.acquireTokenPopup(apiRequest);
        }

        const data = await fetchRapportini(tokenResponse.accessToken, selectedClienteId);
        setRapportini(
          Array.isArray(data)
            ? { cliente: null, rapportini: data, oreTotali: 0 }
            : data
        );
      } catch (err) {
        console.error("Errore nel caricamento dei rapportini:", err);
        setError("Impossibile caricare i rapportini. Verifica la connessione.");
      } finally {
        setLoading(false);
      }
    };

    if (accounts.length > 0 && selectedClienteId) {
      loadRapportini();
    }
  }, [instance, accounts, selectedClienteId]);

  const pageTitle = location.pathname === "/rapportini" ? "I Tuoi Rapportini" : "Commesse";

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="dashboard">
      <Header
        clienti={clienti}
        selectedClienteId={selectedClienteId}
        onSelectCliente={setSelectedClienteId}
      />
      <div className="dashboard-body">
        <aside className="sidebar">
          <nav className="sidebar-nav">
            <NavLink
              to="/commesse"
              className={({ isActive }) =>
                `sidebar-link${isActive ? " active" : ""}`
              }
            >
              Commesse
            </NavLink>
            <NavLink
              to="/rapportini"
              className={({ isActive }) =>
                `sidebar-link${isActive ? " active" : ""}`
              }
            >
              Rapportini
            </NavLink>
          </nav>
        </aside>

        <main className="dashboard-main">
          <div className="dashboard-header">
            <h2>{pageTitle}</h2>
            <button onClick={handleRefresh} className="btn-refresh">
              Aggiorna
            </button>
          </div>

          <div className="dashboard-content">
            {(() => {
              const selectedCliente = clienti.find((c) => c.id === selectedClienteId);
              return (
                <Routes>
                  <Route
                    path="/rapportini"
                    element={<RapportiniTable rapportini={rapportini} loading={loading} error={error || clientError} selectedCliente={selectedCliente} />}
                  />
                  <Route path="/commesse" element={<CommessePage clienteId={selectedClienteId} selectedCliente={selectedCliente} />} />
                  <Route path="*" element={<Navigate to="/commesse" replace />} />
                </Routes>
              );
            })()}
          </div>
        </main>
      </div>
    </div>
  );
}

export default Dashboard;
